import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Currency = 'EUR' | 'USD' | 'TRY' | 'CHF' | 'GBP';

interface CurrencyContextType {
  selectedCurrency: Currency;
  setSelectedCurrency: (currency: Currency) => void;
  currencySymbol: string;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  clearData: () => void;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  'EUR': '€',
  'USD': '$',
  'TRY': '₺',
  'CHF': '₣',
  'GBP': '£'
};

// Default currencies based on language
const getDefaultCurrencyForLanguage = (language: string): Currency => {
  switch (language) {
    case 'tr':
      return 'TRY';
    case 'de':
      return 'EUR';
    case 'en':
    default:
      return 'USD';
  }
};

interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const [selectedCurrency, setSelectedCurrencyState] = useState<Currency>('EUR');
  const [isLoading, setIsLoading] = useState(false);

  // Load currency from localStorage on mount and set default based on language
  useEffect(() => {
    const savedCurrency = localStorage.getItem('selectedCurrency') as Currency;
    const currentLanguage = localStorage.getItem('app_language') || 'en';
    
    if (savedCurrency && Object.keys(CURRENCY_SYMBOLS).includes(savedCurrency)) {
      setSelectedCurrencyState(savedCurrency);
    } else {
      // Set default currency based on current language
      const defaultCurrency = getDefaultCurrencyForLanguage(currentLanguage);
      setSelectedCurrencyState(defaultCurrency);
      localStorage.setItem('selectedCurrency', defaultCurrency);
    }
  }, []);

  const setSelectedCurrency = (currency: Currency) => {
    setIsLoading(true);
    setSelectedCurrencyState(currency);
    localStorage.setItem('selectedCurrency', currency);
    
    // Loading will be stopped when data is fetched
    // setTimeout(() => {
    //   setIsLoading(false);
    // }, 300);
  };

  const clearData = () => {
    // This will be used to clear data when currency changes
  };

  const currencySymbol = CURRENCY_SYMBOLS[selectedCurrency];

  return (
    <CurrencyContext.Provider value={{
      selectedCurrency,
      setSelectedCurrency,
      currencySymbol,
      isLoading,
      setIsLoading,
      clearData
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
